import { key } from 'core-ts-types';
import * as _4 from 'lodash4';
import { APCourse, IAPCourseMeta } from '../../global/builders/ap_course_builder';
import { APCourseGroup, IAPCourseGroupMeta } from '../../global/builders/ap_course_group_builder';
import { CourseGroupSlot, CourseSlot, WildcardSlot } from '../../global/builders/ap_generic_slot_builder';
import { APPlannedCourse, IAPPlannedCourseMeta } from '../../global/builders/ap_planned_course_builder';
import { APTerm } from '../../global/builders/ap_term_builder';
import { IAPPlannedCourseDataSource } from '../../global/datasources/ap_planned_course_datasource';
import { IAPPlannedCourseGroupDatasource } from '../../global/datasources/ap_planned_course_group_datasource';
import { IAPProgramSlotDatasource } from '../../global/datasources/ap_program_slot_datasource';
import { IAPTermsDatasource } from '../../global/datasources/ap_terms_datasource';
import { APModel } from '../../global/models/ap_model';
import { APProgramSlotServant } from '../../global/servants/ap_program_slot_servant';
import { APTermServant } from '../../global/servants/ap_term_servant';
import { LargeCourseGroupSearchResultModel } from '../components/large_course_group/models/large_course_group_search_result_model';

export class APTermCourseDataQueryhelper {
  constructor(
    private ngAPPlannedCoursesDatasource: IAPPlannedCourseDataSource,
    private ngAPProgramSlotDatasource: IAPProgramSlotDatasource,
    private ngAPPlannedCourseGroupDatasource: IAPPlannedCourseGroupDatasource,
    private ngLargeCourseGroupSearchResultModel: LargeCourseGroupSearchResultModel,
    private ngAPTermsDatasource: IAPTermsDatasource,
    private ngAPModel: APModel
  ) {}

  getRequiredDataForTerms() {
    const pastTermCourses = this.getPastTermCourses();
    const pastTermCoursesWithMeta = this.getPastTermCoursesWithMetaInfo();
    const termsWithMeta = this.getTermsWithMetaInfo();
    const futureTerms = this.getFutureTerms();
    const plannedTerms = this.getPlannedTerms();
    const unPlannedTerms = this.getUnPlannedTerms();

    return {
      unPlannedTerms,
      plannedTerms,
      futureTerms,
      pastTermCourses,
      pastTermCoursesWithMeta,
      termsWithMeta,
      plannedCoursesWithMeta: this.ngAPPlannedCoursesDatasource.metasource().derived(),
      programSlotWithMeta: this.ngAPProgramSlotDatasource.metasource().derived()
    };
  }

  getPlannedTerms() {
    const futureTerms = this.getFutureTerms();
    const apModel = this.ngAPModel.read();
    return _4.filter(futureTerms, (term: APTerm) => _4.includes(apModel.planned_term, term.id));
  }

  getUnPlannedTerms() {
    const futureTerms = this.getFutureTerms();
    const apModel = this.ngAPModel.read();
    return _4.sortBy(
      _4.filter(futureTerms, (term: APTerm) => !_4.includes(apModel.planned_term, term.id)),
      'rank'
    );
  }

  getFutureTerms() {
    const termsWithMeta = this.getTermsWithMetaInfo();
    return APTermServant.getFutureTerms(termsWithMeta);
  }

  getTerms() {
    return this.ngAPTermsDatasource.values();
  }

  getTermsWithMetaInfo() {
    return this.ngAPTermsDatasource.metasource().derived();
  }

  getPlannedCourseGroups() {
    const courseGroupProgramSlotIDs = _4.map(
      this.ngAPProgramSlotDatasource.values().filter((programSlot) => programSlot.slot_type === 'GROUP'),
      'id'
    );
    return this.ngAPPlannedCourseGroupDatasource
      .values()
      .filter((plannedCourseGroup) => _4.includes(courseGroupProgramSlotIDs, plannedCourseGroup.program_slot));
  }

  getPlannedCourseGroupsForTerm(termId: number) {
    const plannedCourseGroups = this.getPlannedCourseGroups();

    return _4.filter(
      plannedCourseGroups,
      (plndCourseGroup: APCourseGroup & IAPCourseGroupMeta) => plndCourseGroup.term === termId
    );
  }

  getPlannedWildcardCourseGroupsForTerm(termId: APTerm['id']) {
    const plannedCourseGroupsForTerm = _4.filter(this.ngAPPlannedCourseGroupDatasource.values(), { term: termId });
    return plannedCourseGroupsForTerm.filter((plannedCourseGroup) => {
      const pgmSlot = this.ngAPProgramSlotDatasource.get(plannedCourseGroup.program_slot);
      if (!pgmSlot) {
        return false;
      }
      return APProgramSlotServant.isWildcardProgramSlot(pgmSlot) && !pgmSlot.is_course_planned;
    });
  }

  getPlannedNCRsForTerm(termId: APTerm['id']) {
    const plannedCourseGroupsForTerm = _4.filter(this.ngAPPlannedCourseGroupDatasource.values(), { term: termId });
    return plannedCourseGroupsForTerm.filter((plannedCourseGroup) => {
      const pgmSlot = this.ngAPProgramSlotDatasource.get(plannedCourseGroup.program_slot);
      if (!pgmSlot) {
        return false;
      }
      return APProgramSlotServant.isNCRProgramSlot(pgmSlot);
    });
  }

  getPastTermCourses(): APPlannedCourse[] {
    const pastTermIDs = this.getPastTermIDs();
    return _4.filter(this.ngAPPlannedCoursesDatasource.values(), (plannedCourse) =>
      _4.includes(pastTermIDs, plannedCourse.term)
    );
  }

  getPastTermIDs() {
    return _4.reduce(
      this.ngAPTermsDatasource.read(),
      (ids: number[], term: APTerm) => {
        if (term.is_past_term) {
          ids.push(term.id);
        }
        return ids;
      },
      []
    );
  }

  getPastTermCoursesWithMetaInfo() {
    const pastTermIDs = this.getPastTermIDs();
    return _4.filter(this.ngAPPlannedCoursesDatasource.metasource().derived(), (plannedCourse) =>
      _4.includes(pastTermIDs, plannedCourse.term)
    );
  }

  getPlannedCourseGroup(slotId: key) {
    const plannedCourseGroups = this.getPlannedCourseGroups();
    return _4.find(plannedCourseGroups, { program_slot: slotId });
  }

  getLargeCourseGroupSearchResult(builtGenericSlot: CourseGroupSlot | WildcardSlot, groupGenericSlots: CourseSlot[]) {
    builtGenericSlot.genericSlots = groupGenericSlots;
    const selectedLargeCourseGroup = builtGenericSlot;
    const largeCourseGroupCourses = _4.keyBy(groupGenericSlots, 'course.id');
    const largeCourseGroupSearchResults = {
      ...builtGenericSlot,
      genericSlots: _4.compact(
        _4.map(this.ngLargeCourseGroupSearchResultModel.read().ids, (id) => largeCourseGroupCourses[id])
      )
    };
    return { selectedLargeCourseGroup, largeCourseGroupSearchResults };
  }
}

angular.module('capability-ng-academic-planner').service('ngAPTermCourseDataQueryhelper', APTermCourseDataQueryhelper);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/plan/queries/ap_term_course_data_query_helper.ts